import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ApiError, http } from './api'
import { errorMessage } from './errors'
import type { Me } from './types'

export const authKeys = { me: ['auth', 'me'] as const }

export async function fetchMe(): Promise<Me | null> {
  try {
    return await http.get<Me>('/api/me')
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null
    throw error
  }
}

export function useAuth() {
  return useQuery({ queryKey: authKeys.me, queryFn: fetchMe, retry: false, staleTime: 60_000 })
}

export interface MyPreferencesInput {
  blurAmounts?: boolean
}

export async function updateMyPreferences(input: MyPreferencesInput): Promise<Me> {
  return http.patch<Me>('/api/me/preferences', input)
}

/** Het antwoord is het volledige /api/me-lichaam, dus de cache is een vervanging. */
export function useUpdateMyPreferences() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateMyPreferences,
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.me, data)
      toast.success('Voorkeur opgeslagen')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

export function useIsAdmin(): boolean {
  const { data } = useAuth()
  return data?.user.role === 'ADMIN'
}

/**
 * Financiele leestoegang: facturen, terugkerend, uitbetaaloverzicht en rapportages.
 * Spiegelt de FINANCE-voter in de backend.
 */
export function useCanReadFinance(): boolean {
  const { data } = useAuth()
  return data?.user.role === 'ADMIN' || data?.user.role === 'PARTNER'
}

export interface DevLoginUser {
  email: string
  label: string
  role: string
}

export const devLoginUsers: DevLoginUser[] = [
  { email: 'renko@configurewise.com', label: 'Renko Steenbeek', role: 'Beheerder' },
  { email: 'mike@configurewise.com', label: 'Mike', role: 'Partner' },
  { email: 'jonne@configurewise.com', label: 'Jonne', role: 'Partner' },
  { email: 'elke@configurewise.com', label: 'Elke', role: 'Onderaannemer' },
  { email: 'bram@configurewise.com', label: 'Bram de Vries', role: 'Externe boeker' },
]

export type AuthMode = 'local' | 'entra'

export async function fetchAuthMode(): Promise<AuthMode> {
  try {
    const res = await fetch('/auth/mode', { credentials: 'include' })
    if (!res.ok) return 'entra'
    const data = (await res.json()) as { mode?: string }
    return data.mode === 'local' ? 'local' : 'entra'
  } catch {
    return 'entra'
  }
}

export function useAuthMode() {
  return useQuery({ queryKey: ['auth', 'mode'], queryFn: fetchAuthMode, staleTime: Infinity })
}

export async function localLogin(email: string): Promise<void> {
  const res = await fetch(`/auth/local/login?email=${encodeURIComponent(email)}`, { credentials: 'include' })
  if (!res.ok) throw new ApiError(res.status, { error: { code: 'login_failed', message: 'Inloggen mislukt' } })
}

export async function logout(): Promise<void> {
  await fetch('/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => undefined)
}
